import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GraduationCap, Users, Heart, Search, Calendar, Video, Star, Eye,
  ShieldCheck, MessageCircle, Award, FileText, BookOpen, DollarSign,
  Check, ArrowRight, MapPin, Filter, Globe2, CreditCard, Bell,
  Smartphone, Languages, Mic, Lock, Sparkles,
} from 'lucide-react';
import MiniMapPreview from './MiniMapPreview';
import SafetyDashboardPreview from './SafetyDashboardPreview';
import { useLanguage } from '../../context/LanguageContext';

/**
 * RoleJourney
 * Replaces the heavy 3-scene Cinematic Hero with a clean, role-based
 * "How it works" guide. Each role (Student / Parent / Teacher) gets a
 * tidy 4-step walkthrough — easy on the eye, useful, and conversion-friendly.
 */
const ROLES = [
  {
    key: 'student',
    icon: GraduationCap,
    accent: 'indigo',
    labelAr: 'الطالب',
    labelEn: 'Student',
    descAr: 'ابحث عن المعلم المناسب وابدأ التعلم في دقائق',
    descEn: 'Find the right tutor and start learning in minutes',
    steps: [
      {
        icon: Search,
        titleAr: 'ابحث عن مدرّسك',
        titleEn: 'Find your tutor',
        bodyAr: 'تصفّح المعلمين الموثّقين، فلتر بالمادة، الإمارة، التقييم، أو نمط التدريس (أونلاين/منزل/مركز).',
        bodyEn: 'Browse verified tutors and filter by subject, emirate, rating, or teaching mode.',
        showMapPreview: true,
        chips: [
          { icon: Filter, ar: 'فلاتر ذكية', en: 'Smart filters' },
          { icon: MapPin, ar: 'اختيار الموقع على الخريطة', en: 'Pick location on map' },
          { icon: Languages, ar: 'بحث ثنائي اللغة', en: 'Bilingual search' },
        ],
      },
      {
        icon: Calendar,
        titleAr: 'احجز الموعد المناسب',
        titleEn: 'Book your slot',
        bodyAr: 'شوف توافر المعلم اللحظي واحجز في ثوانٍ. الدفع آمن عبر Stripe.',
        bodyEn: 'See real-time availability and book in seconds. Payments are secured by Stripe.',
        chips: [
          { icon: CreditCard, ar: 'دفع آمن — Stripe & Apple Pay', en: 'Secure pay — Stripe & Apple Pay' },
          { icon: Bell, ar: 'تنبيهات قبل الحصة', en: 'Pre-lesson reminders' },
          { icon: Sparkles, ar: 'حصة تجريبية مجانية', en: 'Free trial lesson' },
        ],
      },
      {
        icon: Video,
        titleAr: 'احضر الدرس الافتراضي',
        titleEn: 'Join the virtual class',
        bodyAr: 'فصل افتراضي بالفيديو والسبورة التفاعلية. تنبيهات قبل الدرس.',
        bodyEn: 'HD video classroom with interactive whiteboard. Reminders before each lesson.',
        chips: [
          { icon: Video, ar: 'فيديو HD + سبورة تفاعلية', en: 'HD video + whiteboard' },
          { icon: Mic, ar: 'صوت كريستال', en: 'Crystal-clear audio' },
          { icon: Smartphone, ar: 'يعمل على الجوال', en: 'Works on mobile' },
        ],
      },
      {
        icon: Star,
        titleAr: 'قيّم وراجع التسجيل',
        titleEn: 'Rate and review',
        bodyAr: 'بعد الدرس: قيّم المعلم وارجع لتسجيل الحصة من لوحتك في أي وقت.',
        bodyEn: 'Rate the lesson and rewatch the recording from your dashboard anytime.',
        chips: [
          { icon: Video, ar: 'مكتبة تسجيلات شخصية', en: 'Personal recording library' },
          { icon: Star, ar: 'تقييم بنجوم', en: '5-star ratings' },
          { icon: ShieldCheck, ar: 'ضمان الجودة', en: 'Quality guarantee' },
        ],
      },
    ],
  },
  {
    key: 'parent',
    icon: Heart,
    accent: 'rose',
    labelAr: 'ولي الأمر',
    labelEn: 'Parent',
    descAr: 'تابع تقدّم أطفالك بأمان وبيئة محمية بالكامل',
    descEn: 'Track your children\'s progress in a fully protected environment',
    steps: [
      {
        icon: Users,
        titleAr: 'أضف أطفالك',
        titleEn: 'Add your children',
        bodyAr: 'أنشئ حساب طفل بمعرّف وكلمة سر بسيطة. لا حاجة لإيميل.',
        bodyEn: 'Create a child account with a simple username and password. No email needed.',
        chips: [
          { icon: Users, ar: 'حسابات أطفال متعددة', en: 'Multiple child accounts' },
          { icon: Lock, ar: 'بدون بريد إلكتروني', en: 'No email required' },
        ],
      },
      {
        icon: ShieldCheck,
        titleAr: 'بيئة آمنة بالذكاء الاصطناعي',
        titleEn: 'AI-safeguarded environment',
        bodyAr: 'كل الرسائل مفحوصة آلياً. تنبيهات لحظية لأي محتوى مشبوه.',
        bodyEn: 'All messages are AI-scanned. Get instant alerts for any concerning content.',
        showSafetyPreview: true,
        chips: [
          { icon: ShieldCheck, ar: 'فحص الرسائل بالذكاء الاصطناعي', en: 'AI message scanning' },
          { icon: Bell, ar: 'تنبيهات لحظية', en: 'Real-time alerts' },
        ],
      },
      {
        icon: Eye,
        titleAr: 'مراقبة صامتة للحصص',
        titleEn: 'Stealth lesson monitoring',
        bodyAr: 'انضم للحصة في أي لحظة دون علم المعلم أو الطفل — Trust by design.',
        bodyEn: 'Drop into any lesson silently without the tutor or child noticing — trust by design.',
        chips: [
          { icon: Eye, ar: 'دخول صامت بالكامل', en: 'Fully silent entry' },
          { icon: Lock, ar: 'صلاحية ولي الأمر فقط', en: 'Parent-only access' },
        ],
      },
      {
        icon: FileText,
        titleAr: 'تقارير ذكية أسبوعية',
        titleEn: 'Smart weekly reports',
        bodyAr: 'ملخّص أسبوعي تلقائي عن الحضور، الجودة، والتقدم — مع توصيات.',
        bodyEn: 'Automated weekly digest covering attendance, quality, and progress — with suggestions.',
        chips: [
          { icon: FileText, ar: 'تقرير PDF أسبوعي', en: 'Weekly PDF report' },
          { icon: Sparkles, ar: 'توصيات مخصصة', en: 'Personalized tips' },
        ],
      },
    ],
  },
  {
    key: 'teacher',
    icon: BookOpen,
    accent: 'emerald',
    labelAr: 'الأستاذ',
    labelEn: 'Tutor',
    descAr: 'ابنِ ملفك المهني، استقبل حجوزات، واحصل على دخل مستقل',
    descEn: 'Build your profile, accept bookings, and earn as an independent tutor',
    steps: [
      {
        icon: Award,
        titleAr: 'أكمل ملفك واطلب التوثيق',
        titleEn: 'Complete profile & request verification',
        bodyAr: 'ارفع شهاداتك واختر المواد، الإمارات، وأنماط التدريس. اعتماد الإدارة في 24 ساعة.',
        bodyEn: 'Upload credentials, pick subjects, emirates, and teaching modes. Admin review within 24h.',
        chips: [
          { icon: Award, ar: 'مؤشر الثقة 100/100', en: 'Trust Score 100/100' },
          { icon: Globe2, ar: 'تغطية كل الإمارات', en: 'All 7 emirates' },
          { icon: MapPin, ar: 'مواقع متعددة', en: 'Multi-location' },
        ],
      },
      {
        icon: Calendar,
        titleAr: 'حدّد جدول توفّرك',
        titleEn: 'Set your availability',
        bodyAr: 'لوحة Sci-Fi لإدارة الأوقات بسهولة. فعّل "متاح الآن" للحصص الفورية.',
        bodyEn: 'Sleek availability planner. Toggle "Available now" for instant lesson requests.',
        chips: [
          { icon: Calendar, ar: 'جدول مرئي', en: 'Visual schedule' },
          { icon: Sparkles, ar: '"متاح الآن" للطلب الفوري', en: '"Available now" toggle' },
        ],
      },
      {
        icon: MessageCircle,
        titleAr: 'تواصل واقبل الحجوزات',
        titleEn: 'Chat & accept bookings',
        bodyAr: 'استقبل رسائل الطلاب، اعتمد الحجوزات، وانطلق للفصل الافتراضي بنقرة واحدة.',
        bodyEn: 'Receive student chats, approve bookings, and join the virtual class with one click.',
        chips: [
          { icon: MessageCircle, ar: 'محادثات آمنة', en: 'Secure chat' },
          { icon: Bell, ar: 'إشعارات الموبايل', en: 'Mobile push notifications' },
        ],
      },
      {
        icon: DollarSign,
        titleAr: 'استلم أرباحك',
        titleEn: 'Get paid',
        bodyAr: 'شفافية كاملة في العمولة. اطلب السحب لحسابك البنكي وقت ما تشاء.',
        bodyEn: 'Transparent commission. Request payouts to your bank account anytime.',
        chips: [
          { icon: DollarSign, ar: 'سحب فوري', en: 'Instant payouts' },
          { icon: ShieldCheck, ar: 'شفافية تامة في العمولة', en: 'Transparent fees' },
        ],
      },
    ],
  },
];

const ACCENT = {
  indigo:  { ring: 'ring-indigo-500/40',  bg: 'bg-indigo-500',   bgSoft: 'bg-indigo-50 dark:bg-indigo-900/20',   text: 'text-indigo-600 dark:text-indigo-300',   line: 'bg-indigo-200 dark:bg-indigo-800', glow: 'shadow-[0_8px_30px_rgba(99,102,241,0.25)]' },
  rose:    { ring: 'ring-rose-500/40',    bg: 'bg-rose-500',     bgSoft: 'bg-rose-50 dark:bg-rose-900/20',       text: 'text-rose-600 dark:text-rose-300',       line: 'bg-rose-200 dark:bg-rose-800',     glow: 'shadow-[0_8px_30px_rgba(244,63,94,0.25)]' },
  emerald: { ring: 'ring-emerald-500/40', bg: 'bg-emerald-500',  bgSoft: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-300', line: 'bg-emerald-200 dark:bg-emerald-800', glow: 'shadow-[0_8px_30px_rgba(16,185,129,0.25)]' },
};

const RoleJourney = ({ language: _legacyLang, isDark = false }) => {
  const { language, localize } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => localize({ ar, en });
  const [activeKey, setActiveKey] = useState('student');
  const active = ROLES.find((r) => r.key === activeKey);
  const acc = ACCENT[active.accent];

  return (
    <section
      className={`${isDark ? 'bg-[#09090B]' : 'bg-slate-50'} py-12 sm:py-16`}
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="role-journey"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400 mb-2">
            {t('كيف تستخدم المنصة', 'How to use the platform')}
          </p>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {t('رحلتك على Learn With Trust', 'Your journey on Learn With Trust')}
          </h2>
          <p className={`mt-3 text-sm sm:text-base max-w-xl mx-auto leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            {t(
              'اختر دورك لترى الخطوات الأربع الواضحة والمميزات المتاحة لك.',
              'Pick your role to see four clear steps and the rich features available to you.'
            )}
          </p>
        </div>

        {/* ╔═══════════════════ JOURNEY FRAME ═══════════════════╗ */}
        <div
          className={`relative rounded-3xl border-2 shadow-xl overflow-hidden ${
            isDark
              ? 'bg-gradient-to-br from-[#0d1117] via-[#161b22] to-[#0d1117] border-white/10'
              : 'bg-gradient-to-br from-white via-slate-50 to-white border-slate-200'
          }`}
          data-testid="role-journey-frame"
        >
          {/* Subtle accent corner glow */}
          <div className={`absolute -top-20 -${isAr ? 'left' : 'right'}-20 w-60 h-60 rounded-full opacity-30 blur-3xl pointer-events-none ${acc.bg}`} />

          <div className="relative p-5 sm:p-8">
            {/* Role Tabs */}
            <div
              className={`flex flex-wrap justify-center gap-1.5 mb-6 p-1.5 rounded-2xl border max-w-xl mx-auto ${
                isDark ? 'bg-[#0d1117] border-white/5' : 'bg-white border-slate-200'
              }`}
              data-testid="role-tabs"
              role="tablist"
            >
              {ROLES.map((role) => {
                const Icon = role.icon;
                const isActive = role.key === activeKey;
                const a = ACCENT[role.accent];
                return (
                  <button
                    key={role.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveKey(role.key)}
                    className={`flex-1 min-w-[100px] inline-flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                      isActive
                        ? `${a.bg} text-white ${a.glow}`
                        : isDark
                          ? 'text-slate-400 hover:text-white hover:bg-white/5'
                          : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                    data-testid={`role-tab-${role.key}`}
                  >
                    <Icon className="w-4 h-4" />
                    {localize({ ar: role.labelAr, en: role.labelEn })}
                  </button>
                );
              })}
            </div>

            {/* Active role description */}
            <p className={`text-center text-sm mb-6 max-w-xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {localize({ ar: active.descAr, en: active.descEn })}
            </p>

            {/* Steps grid */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeKey}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
                data-testid={`role-steps-${activeKey}`}
              >
                {active.steps.map((step, idx) => {
                  const StepIcon = step.icon;
                  const isLast = idx === active.steps.length - 1;
                  return (
                    <div
                      key={idx}
                      className={`relative rounded-2xl p-4 sm:p-5 border ${
                        isDark ? 'bg-[#161b22] border-[#30363d]' : 'bg-white border-slate-200 hover:border-slate-300'
                      } transition-colors`}
                      data-testid={`role-step-${activeKey}-${idx + 1}`}
                    >
                      {/* Step number */}
                      <div className={`absolute -top-2.5 ${isAr ? 'right-4' : 'left-4'} inline-flex items-center justify-center w-7 h-7 rounded-full ${acc.bg} text-white text-[11px] font-black shadow-md ring-4 ${isDark ? 'ring-[#0d1117]' : 'ring-white'}`}>
                        {String(idx + 1).padStart(2, '0')}
                      </div>

                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-xl ${acc.bgSoft} ${acc.text} flex items-center justify-center mb-3 mt-1`}>
                        <StepIcon className="w-5 h-5" />
                      </div>

                      {/* Title + body */}
                      <h3 className={`text-sm sm:text-base font-bold mb-1.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {localize({ ar: step.titleAr, en: step.titleEn })}
                      </h3>
                      <p className={`text-xs sm:text-[13px] leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        {localize({ ar: step.bodyAr, en: step.bodyEn })}
                      </p>

                      {/* Feature chips — make options visible */}
                      {step.chips && step.chips.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`role-step-chips-${activeKey}-${idx + 1}`}>
                          {step.chips.map((chip, ci) => {
                            const ChipIcon = chip.icon;
                            return (
                              <span
                                key={ci}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] sm:text-[11px] font-semibold border ${
                                  isDark
                                    ? 'bg-white/[0.03] border-white/10 text-slate-300'
                                    : 'bg-slate-50 border-slate-200 text-slate-700'
                                }`}
                              >
                                <ChipIcon className={`w-3 h-3 ${acc.text}`} />
                                {localize({ ar: chip.ar, en: chip.en })}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Optional inline mini-map preview (Student step 1) */}
                      {step.showMapPreview && (
                        <MiniMapPreview language={language} isDark={isDark} />
                      )}

                      {/* Optional inline safety dashboard preview (Parent step 2) */}
                      {step.showSafetyPreview && (
                        <SafetyDashboardPreview language={language} isDark={isDark} />
                      )}

                      {/* Connector chevron — desktop only */}
                      {!isLast && (
                        <div
                          className={`hidden lg:flex absolute top-1/2 -translate-y-1/2 ${isAr ? '-left-3' : '-right-3'} w-6 h-6 items-center justify-center rounded-full ${acc.bgSoft} ${acc.text} z-10`}
                          aria-hidden
                        >
                          <ArrowRight className={`w-3.5 h-3.5 ${isAr ? 'rotate-180' : ''}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </motion.div>
            </AnimatePresence>

            {/* Footer trust line — inside the frame */}
            <div className={`mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mx-auto block w-fit ${
              isDark ? 'bg-emerald-900/20 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
            }`}>
              <Check className="w-4 h-4" />
              {t(
                'كل الخطوات مغطّاة بضمان الثقة الكامل — Trust by design.',
                'Every step is covered by our full Trust guarantee — Trust by design.'
              )}
            </div>
          </div>
        </div>
        {/* ╚═══════════════════════════════════════════════════════╝ */}
      </div>
    </section>
  );
};

export default RoleJourney;
